import { classnames, isServer } from '@/helpers';

import {
  cloneElement,
  forwardRef,
  isValidElement,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { composeHandlers } from './DropdownMenu.helpers';
import { DropdownMenuContext, useDropdown, useDropdownMenuManager } from './DropdownMenu.hooks';
import { DropdownManagerProps, TriggerProps } from './DropdownMenu.types';

function DropdownMenu({ children, ...props }: { children: ReactNode } & DropdownManagerProps) {
  const manager = useDropdownMenuManager(props);
  return <DropdownMenuContext.Provider value={manager}>{children}</DropdownMenuContext.Provider>;
}

const Trigger = forwardRef<HTMLElement, TriggerProps>(function Trigger({ asChild, children }, _ref) {
  const { contentId, isOpen, toggleOpen, triggerRef, placement } = useDropdown();

  if (asChild) {
    if (!isValidElement(children)) {
      console.error('DropdownMenuTrigger with `asChild` expects a single React element child.');
      return null;
    }

    const childProps: any = children.props || {};
    const injectedProps = {
      ref: triggerRef,
      'aria-haspopup': 'menu',
      'aria-expanded': isOpen,
      'aria-controls': contentId,
      'data-state': isOpen ? 'open' : 'closed',
      'data-placement': placement,
      onClick: composeHandlers(childProps.onClick, toggleOpen),
    };

    return cloneElement(children, injectedProps);
  }

  // Fallback: render a native button if not using asChild
  return (
    <button
      ref={triggerRef}
      type="button"
      aria-haspopup="menu"
      aria-expanded={isOpen}
      aria-controls={contentId}
      data-state={isOpen ? 'open' : 'closed'}
      data-placement={placement}
      onClick={toggleOpen}>
      Open
    </button>
  );
});

const Content = ({ children, className }: { children: ReactNode; className?: string }) => {
  const { contentId, isOpen, close, triggerRef, placement, setPlacement } = useDropdown();
  const contentRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({ position: 'absolute' });

  const measureAndPlace = useCallback(() => {
    const trigger = triggerRef.current;
    const content = contentRef.current;
    if (!trigger || !content) return;

    const rect = trigger.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;
    content.style.maxHeight = '';
    const naturalHeight = content.offsetHeight;

    const spaceBelow = vh - rect.bottom; // viewport space
    const spaceAbove = rect.top;

    const shouldFlipUp = naturalHeight > spaceBelow && spaceAbove > spaceBelow;

    // Compute base x/width
    const left = rect.left + scrollX;
    const minWidth = rect.width;

    if (shouldFlipUp) {
      const maxHeight = Math.max(0, spaceAbove - 8); // 8px safety
      setPlacement('top');
      setStyle({
        position: 'absolute',
        top: rect.top + scrollY - Math.min(naturalHeight, maxHeight),
        left,
        minWidth,
        zIndex: 1000,
        maxHeight,
        overflowY: 'auto',
      });
    } else {
      // place below
      const maxHeight = Math.max(0, spaceBelow - 8);
      setPlacement('bottom');
      setStyle({
        position: 'absolute',
        top: rect.bottom + scrollY,
        left,
        minWidth,
        zIndex: 1000,
        maxHeight,
        overflowY: 'auto',
      });
    }

    // Clamp left if needed (using measured width)
    const width = content.offsetWidth || minWidth;
    const maxLeft = Math.max(0, vw - width - 8) + scrollX;
    const clampedLeft = Math.min(style.left ? +style.left : left, maxLeft);
    setStyle((s) => ({ ...s, left: clampedLeft }));
  }, [triggerRef, style.left]);

  /**
   * Effect to measure and place the dropdown content
   * without flicker using layout effect
   */
  useLayoutEffect(() => {
    if (!isOpen) return;
    measureAndPlace();
    const onResize = () => measureAndPlace();
    const onScroll = () => measureAndPlace();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, { passive: true });

    /**
     * if content hight changes we need to re calculate
     */
    const ro = new ResizeObserver(() => measureAndPlace());
    if (contentRef.current) ro.observe(contentRef.current);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, [isOpen, measureAndPlace]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (!contentRef?.current?.contains(e?.target as Node)) {
        close();
      }
    };

    document?.addEventListener('mousedown', handleClickOutside);

    return () => {
      document?.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const content = (
    <div
      role="menu"
      className={classnames(className)}
      style={style}
      tabIndex={-1}
      id={contentId}
      ref={contentRef}
      data-state={isOpen ? 'open' : 'closed'}
      data-placement={placement}>
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
        className="z-10">
        {children}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

const Item = ({
  children,
  className,
  disabled,
  closeOnSelect = true,
  onSelect,
  role = 'menuitem',
  checked,
}: {
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  closeOnSelect?: boolean;
  onSelect?: () => void;
  role?: 'menuitem' | 'menuitemradio' | 'menuitemcheckbox';
  checked?: boolean;
}) => {
  const { isOpen, close, moveFocus, registerItem, focusFirstItem } = useDropdown();

  const ref = useRef<HTMLDivElement>(null);
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => registerItem(ref, setTabIndex), [registerItem]);

  useEffect(() => {
    if (isOpen) {
      focusFirstItem();
    }
  }, [isOpen, focusFirstItem]);

  const handleOnClick = (_: SyntheticEvent) => {
    if (disabled) return;
    onSelect?.();
    if (closeOnSelect) close();
  };

  return (
    <div
      ref={ref}
      className={classnames(className, 'relative z-10', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}
      role={role}
      aria-checked={checked}
      tabIndex={tabIndex}
      aria-disabled={disabled || undefined}
      data-disabled={disabled ? '' : undefined}
      onClick={handleOnClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleOnClick(e);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          moveFocus(1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          moveFocus(-1);
        } else if (e.key === 'Home') {
          e.preventDefault();
          moveFocus('start');
        } else if (e.key === 'End') {
          e.preventDefault();
          moveFocus('end');
        } else if (e.key === 'Tab') {
          e.preventDefault();
          close();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          close();
        }
      }}
      style={{ userSelect: 'none' }}>
      {children}
    </div>
  );
};

DropdownMenu.Content = Content;
DropdownMenu.Trigger = Trigger;
DropdownMenu.Item = Item;

export default DropdownMenu;
